import { useRef, useState, useEffect, useMemo } from "react";
import { FolderOpen, ChevronDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { AgentGroup } from "@/types/agent/agent-group";
import { Agent } from "@/types/agent/agent";
import { useAssignAgentToGroup } from "@/hooks/agent";

interface GroupSelectorProps {
  agentId: string;
  currentGroupId: number | null;
  groups: AgentGroup[];
  currentGroup?: AgentGroup | null;
  agent: Agent;
  disabled?: boolean;
  brandColor?: string;
  secondaryColor?: string;
  gradientEnabled?: boolean;
  gradientAngle?: number;
}

const DEFAULT_PRIMARY = "#4E9CFF";

export const GroupSelector = ({
  agentId,
  currentGroupId,
  groups,
  currentGroup: currentGroupProp,
  agent,
  disabled,
  brandColor = DEFAULT_PRIMARY,
}: GroupSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const assignMutation = useAssignAgentToGroup();

  const currentGroup = currentGroupProp || groups.find((g) => g.id === currentGroupId);

  const isPersonalAgent = agent.user_id === user?.id;

  // Filter groups based on agent ownership
  const availableGroups = useMemo(() => {
    return groups.filter((group) => {
      const isPersonalGroup = group.user_id === user?.id;
      // Personal agents can only use personal groups
      // Team agents can use team groups
      return isPersonalAgent ? isPersonalGroup : !isPersonalGroup;
    });
  }, [groups, user?.id, isPersonalAgent]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = async (groupId: number | null) => {
    if (groupId === currentGroupId) {
      setIsOpen(false);
      return;
    }
    try {
      await assignMutation.mutateAsync({ agentId, groupId });
      setIsOpen(false);
    } catch (error: any) {
      // Error is already handled by mutation, no need to double toast
      // The mutation's onError in the hook should handle toasting
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={disabled || assignMutation.isPending}
        className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full transition-all disabled:opacity-50 shadow-sm border bg-white"
        style={{
          color: brandColor,
          borderColor: `${brandColor}55`,
        }}
      >
        {assignMutation.isPending ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <FolderOpen className="w-3 h-3" />
        )}
        <span className="truncate max-w-[100px]">
          {currentGroup?.name || "Ungrouped"}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-20"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 px-2 py-1">
              Move to Group
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(null);
              }}
              className="w-full flex items-center justify-between px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors"
            >
              <span>Ungrouped</span>
              {currentGroupId === null && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </button>

            {availableGroups.length > 0 && (
              <div className="border-t my-1 mx-2 border-gray-100" />
            )}

            {availableGroups.map((group) => (
              <button
                key={group.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(group.id);
                }}
                disabled={assignMutation.isPending}
                className="w-full flex items-center justify-between px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="truncate flex items-center gap-2">
                  {group.name}
                  {assignMutation.isPending && currentGroupId !== group.id && (
                    <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                  )}
                </span>
                {currentGroupId === group.id && (
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </button>
            ))}

            {availableGroups.length === 0 && (
              <p className="px-2 py-2 text-xs text-gray-400">
                {isPersonalAgent
                  ? "No personal groups yet. Create one from the tabs above."
                  : "No team groups available."}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
